import React, { createContext, useCallback, useReducer } from "react";
import { StoreInterface, ActionInterface } from "./StoreInterface";
import { REDUCER_ACTIONS, ENVIRONMENTS, CLE_TYPES } from "../constants/index";
import { SubscriptionDetails, TwilioEntryConfiguration } from "../models/Subscription";
import { tryJsonParse } from "../utils";
import { AuthRedirect } from "../components/AuthRedirect";

const initialStore: StoreInterface = { environments: [], tokenCacheNotFound: false };
const store = createContext(initialStore);

interface StateProviderProps {
	children: any;
}

/**
 * This is the state provider for our global state management.
 * This component should wrap over the base of our react package and allow us to maintain state globally.
 * @param children
 */
const StateProvider: React.FC<StateProviderProps> = (props: StateProviderProps) => {
	const [state, dispatch] = useReducer((state: StoreInterface, action: ActionInterface) => {
		switch (action.type) {
			case REDUCER_ACTIONS.SET_SUBSCRIPTION: {
				let environmentList = [];
				if (action.value) {
					let environments = action.value
						? tryJsonParse(action.value.subscription!.subscriptionAdditionalConfiguration, {
								SubscriptionConfigurations: [],
							}).SubscriptionConfigurations
						: [];
					//if environments not found in new format try the old format.
					if (environments === undefined) {
						environments = tryJsonParse(action.value.subscription!.subscriptionAdditionalConfiguration, []);
					}

					switch (CLE_TYPES[action.value.product.cleType]) {
						case "Twilio": {
							environmentList = environments.map((e: TwilioEntryConfiguration) => ({
								displayString: ENVIRONMENTS[e.Environment].text,
								valueString: e.AccountSid,
							}));
						}
					}
				}
				return { ...state, subscription: action.value, environments: environmentList };
			}
			case REDUCER_ACTIONS.SET_PRODUCTID:
				return { ...state, productId: action.value };
			case REDUCER_ACTIONS.TOKEN_CACHE_NOT_FOUND:
				return { ...state, tokenCacheNotFound: true };
			default:
				return state;
		}
	}, initialStore);

	const value = {
		...state,
		setSubscription: useCallback((value: SubscriptionDetails | undefined) => {
			dispatch({ type: REDUCER_ACTIONS.SET_SUBSCRIPTION, value });
		}, []),
		getEnvironmentByValue: useCallback(
			(valueString: string) => {
				return state.environments.find((e) => e.valueString === valueString);
			},
			[state.environments],
		),
		setProductId: useCallback((value) => {
			dispatch({ type: REDUCER_ACTIONS.SET_PRODUCTID, value });
		}, []),
	};

	return (
		<store.Provider value={value}>
			<AuthRedirect
				isOpen={state.tokenCacheNotFound}
				tokenCacheNotFoundAction={() => dispatch({ type: REDUCER_ACTIONS.TOKEN_CACHE_NOT_FOUND })}
			>
				{props.children}
			</AuthRedirect>
		</store.Provider>
	);
};

export { store, StateProvider };
